import { useEffect, useState } from 'react'
import { passkeysSupported, platformAuthenticatorAvailable, register, signIn } from '../lib/auth'
import { t } from '../lib/i18n'

/**
 * Sign in, or create an account. Passkeys only.
 *
 * There is no password field and no "forgot password" link, because there is no
 * password. That is stated on the screen rather than left for someone to
 * discover by looking for the missing control.
 */
export default function SignIn({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  /*
   * On a closed deployment the "create an account" affordance is hidden, because
   * for everyone except the owner it leads only to a 403. Hiding it is presentation,
   * NOT the control: /api/auth/register/options refuses a non-owner handle whatever
   * the client renders, and the suite asserts that.
   *
   * The owner still has to claim the account once, and that uses this same form, so
   * ?claim=1 brings it back. Nothing is protected by its absence — the setup token
   * is still what makes the claim succeed.
   */
  const claimRequested = new URLSearchParams(window.location.search).has('claim')
  const [signupOpen, setSignupOpen] = useState(true)
  const [mode, setMode] = useState<'in' | 'up'>('in')
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [setupToken, setSetupToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether WebAuthn exists at all decides what is shown; whether there is a
  // BUILT-IN authenticator only decides how the hint is worded.
  const supported = passkeysSupported()
  const [hasPlatform, setHasPlatform] = useState<boolean | null>(null)

  useEffect(() => {
    void platformAuthenticatorAvailable().then(setHasPlatform)
  }, [])

  useEffect(() => {
    // Health already reports the deploy's posture; no extra endpoint for this.
    void fetch('/api/health')
      .then((r) => r.json() as Promise<{ signup?: string }>)
      .then((h) => setSignupOpen(h.signup !== 'closed'))
      // A health check that cannot answer should not strand the owner mid-claim.
      .catch(() => setSignupOpen(true))
  }, [])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await onSignedIn()
    } catch (err) {
      /*
       * A cancelled ceremony is not an error worth alarming anyone about — the
       * user dismissed a system sheet. Everything else gets the real message.
       */
      const name = (err as { name?: string }).name
      if (name === 'NotAllowedError' || name === 'AbortError') setError(t('auth.cancelled'))
      else setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!supported) {
    return <section className="card"><p>{t('auth.noPasskeySupport')}</p></section>
  }

  return (
    <section className="card">
      <h1>{mode === 'in' ? t('app.signIn') : t('auth.createAccount')}</h1>

      <label>
        {t('auth.handle')}
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value.toLowerCase())}
          autoComplete="username webauthn"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="yourname"
        />
      </label>

      {mode === 'up' && (
        <>
          <label>
            {t('auth.displayName')}
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
          </label>
          <label>
            {t('auth.setupToken')} <span className="muted small">— only for the first owner account</span>
            <input value={setupToken} onChange={(e) => setSetupToken(e.target.value)} autoComplete="off" />
          </label>
        </>
      )}

      <button
        className="primary"
        disabled={busy || (mode === 'up' && !handle)}
        onClick={() =>
          run(() =>
            mode === 'in'
              ? signIn(handle || undefined)
              : register({ handle, displayName, setupToken: setupToken || undefined }),
          )
        }
      >
        {busy ? '…' : mode === 'in' ? t('auth.usePasskey') : t('auth.createAccount')}
      </button>

      {error && <p className="error" role="alert">{error}</p>}

      <p className="muted small">
        {mode === 'in' ? (
          (signupOpen || claimRequested) && (
            <>No account? <button className="link" onClick={() => setMode('up')}>{t('auth.createAccount')}</button></>
          )
        ) : (
          <>Already have one? <button className="link" onClick={() => setMode('in')}>{t('app.signIn')}</button></>
        )}
      </p>

      <p className="muted small">
        There is no password. {hasPlatform === false
          ? 'Use a security key, your password manager, or your phone.'
          : 'Your passkey — Face ID, a fingerprint, your password manager or a security key — is the only way in.'}
      </p>
    </section>
  )
}
